import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  CheckCircle,
  HardDrive,
  Link as LinkIcon,
  ServerCog,
  Users,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn, formatLatency } from '@/lib/utils'
import {
  getDashboardAlerts,
  getDashboardSummary,
  type AdminDashboardAlert,
  type AdminDashboardAlertsResponse,
  type AdminDashboardSummary,
} from '@/api/adminDashboardApi'

function formatRate(rate: number, precision = 2): string {
  if (!Number.isFinite(rate)) return '—'
  return `${(rate * 100).toFixed(precision)}%`
}

function formatNumber(value: number, digits = 0): string {
  if (!Number.isFinite(value)) return '—'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })
}

function DegradationBadge({ degraded, text }: { degraded: boolean; text: string }) {
  return degraded
    ? (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="size-3" />
        {text}
      </Badge>
    )
    : (
      <Badge variant="default" className="gap-1">
        <CheckCircle className="size-3" />
        {text}
      </Badge>
    )
}

function RouteabilityBadge({ routable }: { routable: boolean }) {
  return (
    <Badge variant={routable ? 'default' : 'secondary'} className={cn(routable ? 'bg-emerald-600/20 text-emerald-700' : '')}>
      {routable ? '✓' : '✗'}
    </Badge>
  )
}

function KpiCard({ label, value, subValue, className = '' }: { label: string; value: string; subValue?: string; className?: string }) {
  return (
    <Card className={cn('rounded-lg', className)}>
      <CardHeader className="pb-3">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl tracking-tight">{value}</CardTitle>
      </CardHeader>
      {subValue ? <CardContent className="pt-0 text-sm text-muted-foreground">{subValue}</CardContent> : null}
    </Card>
  )
}

const alertSeverityRank: Record<string, number> = {
  critical: 0,
  warning: 1,
}

function localizedAlertDetail(
  alert: AdminDashboardAlert,
  data: AdminDashboardAlertsResponse,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const queue = data.queues?.find((item) => item.queue === alert.queue)
  switch (alert.id) {
    case 'queue_dead':
      return t('adminDashboard.alerts.items.queueDead.detail', { queue: alert.queue ?? '—', value: formatNumber(alert.value) })
    case 'queue_backlog':
      return t('adminDashboard.alerts.items.queueBacklog.detail', { queue: alert.queue ?? '—', value: formatNumber(alert.value) })
    case 'failure_concentrated':
      return t('adminDashboard.alerts.items.failureConcentrated.detail', {
        queue: alert.queue ?? '—',
        reason: queue?.failure_dominant || t('adminDashboard.unknown'),
        share: formatRate(queue?.failure_share ?? alert.value, 0),
        samples: formatNumber(queue?.failure_samples ?? 0),
      })
    case 'tts_primary_failing':
      return t('adminDashboard.alerts.items.ttsPrimaryFailing.detail', {
        primaryFailures: formatNumber(data.tts_fallback?.primary_failures ?? alert.value),
        fallbackServed: formatNumber(data.tts_fallback?.fallback_served ?? 0),
      })
    default:
      return alert.detail
  }
}

function localizedAlertTitle(alert: AdminDashboardAlert, t: ReturnType<typeof useTranslation>['t']): string {
  const titleKeys: Record<string, string> = {
    queue_dead: 'adminDashboard.alerts.items.queueDead.title',
    queue_backlog: 'adminDashboard.alerts.items.queueBacklog.title',
    failure_concentrated: 'adminDashboard.alerts.items.failureConcentrated.title',
    tts_primary_failing: 'adminDashboard.alerts.items.ttsPrimaryFailing.title',
  }
  const key = titleKeys[alert.id]
  return key ? t(key) : alert.title
}

export function AdminAlertsSection({ data }: { data?: AdminDashboardAlertsResponse }) {
  const { t } = useTranslation()
  if (!data) return null

  const alerts = [...(data.alerts ?? [])].sort(
    (left, right) => (alertSeverityRank[left.severity] ?? 2) - (alertSeverityRank[right.severity] ?? 2),
  )
  const queues = data.queues ?? []

  return (
    <div className="space-y-6">
      {alerts.length ? (
        <section className="space-y-3" aria-labelledby="admin-active-alerts-title">
          <h2 id="admin-active-alerts-title" className="text-lg font-semibold">{t('adminDashboard.alerts.title')}</h2>
          <div className="space-y-2">
            {alerts.map((alert, index) => {
              const critical = alert.severity === 'critical'
              return (
                <Alert
                  key={`${alert.id}-${alert.queue ?? 'global'}-${index}`}
                  variant={critical ? 'destructive' : 'warning'}
                  className={cn(critical ? 'text-destructive' : '')}
                >
                  <AlertTriangle />
                  <AlertTitle>{localizedAlertTitle(alert, t)}</AlertTitle>
                  <AlertDescription>
                    <p>{localizedAlertDetail(alert, data, t)}</p>
                  </AlertDescription>
                </Alert>
              )
            })}
          </div>
        </section>
      ) : null}

      <section className="space-y-3" aria-labelledby="admin-gonker-queues-title">
        <div>
          <h2 id="admin-gonker-queues-title" className="text-lg font-semibold">{t('adminDashboard.queues.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('adminDashboard.queues.description')}</p>
        </div>
        <Card className="rounded-lg">
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminDashboard.queues.queue')}</TableHead>
                  <TableHead className="text-right">{t('adminDashboard.queues.ready')}</TableHead>
                  <TableHead className="text-right">{t('adminDashboard.queues.leased')}</TableHead>
                  <TableHead className="text-right">{t('adminDashboard.queues.dead')}</TableHead>
                  <TableHead>{t('adminDashboard.queues.failureDominant')}</TableHead>
                  <TableHead className="text-right">{t('adminDashboard.queues.failureShare')}</TableHead>
                  <TableHead className="text-right">{t('adminDashboard.queues.failureSamples')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queues.length ? queues.map((queue) => (
                  <TableRow key={queue.queue}>
                    <TableCell className="font-mono">{queue.queue}</TableCell>
                    <TableCell className="text-right">{formatNumber(queue.ready)}</TableCell>
                    <TableCell className="text-right">{formatNumber(queue.leased)}</TableCell>
                    <TableCell className={cn('text-right', queue.dead > 0 ? 'font-semibold text-destructive' : '')}>{formatNumber(queue.dead)}</TableCell>
                    <TableCell>{queue.failure_dominant || '—'}</TableCell>
                    <TableCell className="text-right">{queue.failure_samples > 0 ? formatRate(queue.failure_share, 0) : '—'}</TableCell>
                    <TableCell className="text-right">{formatNumber(queue.failure_samples)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      {t('adminDashboard.queues.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}

export default function AdminDashboard() {
  const { t } = useTranslation()
  const query = useQuery({
    queryKey: ['admin-dashboard-summary'],
    queryFn: getDashboardSummary,
    staleTime: 10_000,
  })
  const alertsQuery = useQuery({
    queryKey: ['admin-dashboard-alerts'],
    queryFn: getDashboardAlerts,
    staleTime: 10_000,
    retry: false,
  })

  const data = query.data as AdminDashboardSummary | undefined
  const sortedRoles = useMemo(() => {
    if (!data?.role_routable) return []
    return [...data.role_routable].sort((a, b) => a.role.localeCompare(b.role))
  }, [data?.role_routable])

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="space-y-4">
        <Button asChild size="sm" variant="outline">
          <Link to="/admin">
            <ArrowLeft className="size-4" />
            {t('adminDashboard.goBack')}
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">{t('adminDashboard.title')}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('adminDashboard.subtitle')}</p>
        </div>
      </div>

      <AdminAlertsSection data={alertsQuery.data} />

      {query.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : query.isError ? (
        <Card className="rounded-lg border-destructive/30 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">{t('adminDashboard.loadFailed')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-destructive">{String((query.error as Error).message || t('adminDashboard.loadFailed'))}</CardContent>
        </Card>
      ) : data ? (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">{t('adminDashboard.users.title')}</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <KpiCard label={t('adminDashboard.users.total')} value={formatNumber(data.users.total)} />
              <KpiCard label={t('adminDashboard.users.internal')} value={formatNumber(data.users.internal)} />
              <KpiCard label={t('adminDashboard.users.admin')} value={formatNumber(data.users.admin)} />
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">{t('adminDashboard.generation.title')}</h2>
            <Card className="rounded-lg">
              <CardContent className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
                <KpiCard label={t('adminDashboard.generation.pending')} value={formatNumber(data.generation.pending)} />
                <KpiCard label={t('adminDashboard.generation.running')} value={formatNumber(data.generation.running)} />
                <KpiCard label={t('adminDashboard.generation.done')} value={formatNumber(data.generation.done)} />
                <KpiCard label={t('adminDashboard.generation.failed')} value={formatNumber(data.generation.failed)} />
                <KpiCard label={t('adminDashboard.generation.partial')} value={formatNumber(data.generation.partial_result)} />
                <KpiCard label={t('adminDashboard.generation.needsReview')} value={formatNumber(data.generation.needs_review)} />
                <KpiCard
                  label={t('adminDashboard.generation.successRate')}
                  value={formatRate(data.generation.success_rate, 2)}
                  subValue={t('adminDashboard.generation.avgLatency', {
                    value: formatNumber(data.generation.avg_latency_ms, 1),
                  })}
                />
                <KpiCard
                  label={t('adminDashboard.generation.partialRate')}
                  value={formatRate(data.generation.partial_rate, 2)}
                />
              </CardContent>
            </Card>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">{t('adminDashboard.llmHealth.title')}</h2>
              <Button size="sm" variant="outline" asChild>
                <Link to="/admin/models">
                  <LinkIcon className="size-4" />
                  {t('adminDashboard.llmHealth.openModels')}
                </Link>
              </Button>
            </div>
            <Card className="rounded-lg">
              <CardContent className="overflow-x-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('adminDashboard.llmHealth.model')}</TableHead>
                      <TableHead className="text-right">{t('adminDashboard.llmHealth.count')}</TableHead>
                      <TableHead className="text-right">{t('adminDashboard.llmHealth.successRate')}</TableHead>
                      <TableHead className="text-right">{t('adminDashboard.llmHealth.p90Latency')}</TableHead>
                      <TableHead className="text-right">{t('adminDashboard.llmHealth.tps')}</TableHead>
                      <TableHead className="text-right">{t('adminDashboard.llmHealth.baselineTps')}</TableHead>
                      <TableHead className="text-right">{t('adminDashboard.llmHealth.state')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.llm_health.length ? data.llm_health.map((row) => (
                      <TableRow key={row.model_alias}>
                        <TableCell className="font-medium">{row.model_alias}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.count)}</TableCell>
                        <TableCell className="text-right">{formatRate(row.success_rate)}</TableCell>
                        <TableCell className="text-right">{formatLatency(row.p90_latency_ms)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.tps, 2)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.baseline_tps, 2)}</TableCell>
                        <TableCell className="text-right">
                          {row.degraded ? (
                            <span className="inline-flex items-center gap-2 text-destructive">
                              <DegradationBadge degraded text={t('adminDashboard.llmHealth.degraded')} />
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2 text-emerald-700">
                              <DegradationBadge degraded={false} text={t('adminDashboard.llmHealth.normal')} />
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                          {t('adminDashboard.empty')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <Card className="rounded-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="size-4 text-primary" />
                  {t('adminDashboard.roles.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('adminDashboard.roles.role')}</TableHead>
                      <TableHead className="text-right">{t('adminDashboard.roles.routable')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRoles.length ? sortedRoles.map((item) => (
                      <TableRow key={item.role}>
                        <TableCell className="font-mono">{item.role}</TableCell>
                        <TableCell className="text-right">
                          <RouteabilityBadge routable={item.routable} />
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={2} className="py-6 text-center text-muted-foreground">
                          {t('adminDashboard.empty')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="rounded-lg">
              <CardHeader className="gap-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ServerCog className="size-4 text-primary" />
                  {t('adminDashboard.backendHealth.title')}
                </CardTitle>
                <CardDescription>{t('adminDashboard.backendHealth.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <KpiCard label={t('adminDashboard.backendHealth.ok')} value={formatNumber(data.backend_health.ok)} />
                  <KpiCard label={t('adminDashboard.backendHealth.failed')} value={formatNumber(data.backend_health.failed)} />
                  <KpiCard label={t('adminDashboard.backendHealth.unknown')} value={formatNumber(data.backend_health.unknown)} />
                </div>
                <Button asChild size="sm" variant="outline" className="w-full justify-center">
                  <Link to="/admin/models">
                    <BarChart3 className="size-4" />
                    {t('adminDashboard.backendHealth.openModels')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </section>

          <section className="space-y-3">
            <Card className="rounded-lg">
              <CardHeader className="gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <HardDrive className="size-4 text-primary" />
                  {t('adminDashboard.storage.title')}
                </CardTitle>
                <CardDescription>{t('adminDashboard.storage.description')}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">{t('adminDashboard.storage.backend')}</div>
                  <div className="mt-1 text-sm font-semibold">{data.storage.backend || t('adminDashboard.unknown')}</div>
                </div>
                <div className="grid gap-2 text-sm">
                  {Object.entries(data.storage.buckets).map(([name, bucket]) => (
                    <div key={name} className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
                      <span className="text-muted-foreground">{name}</span>
                      <span className="font-mono">{bucket || '-'}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        </>
      ) : null}
    </div>
  )
}
